const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/eCheckin/components/ECheckinSections.tsx';
import * as React from 'react';
import { forwardRef, ReactElement, useEffect, useState } from 'react';
import _ from 'lodash';
import { useConnectToChildCIS4mComponents } from '@cistec/common_web/modules/CIS4m/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { Button, useTheme } from '@mui/material';
import { validateForm } from '@cistec/common_web/modules/CIS4m/utils/form';
import {
    actionRemoveCIS4m,
    actionRemoveFromCIS4mDataObject,
    actionSaveCIS4m,
    actionSetInCIS4mDataObject,
    actionValidateAndSaveCIS4m,
} from '@cistec/common_web/modules/CIS4m/actions';
import { actionFinalizeECheckin, actionUpdateECheckin } from 'App/actions/eCheckinItems';
import { css, cx } from '@emotion/css';
import { Theme } from '@mui/material/styles';
import { selectSelectedECheckin } from '../store';
import { makeSelectFinalizeECheckinRequestStatus, useECheckinItem } from 'App/store/eCheckinItems';
import CloseIcon from '@mui/icons-material/Close';
import textMap from '../translations';
import { useIntl } from 'react-intl';
import { ProgressStepper } from './ProgressStepper';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { ECheckinWorkItem } from '@cistec/common_web/defs/kisim';
import { RequestStatus } from '@cistec/common_web/utils/redux4Requests';
import { CIS4mData } from '@cistec/common_web/modules/CIS4m/types';
import { useMadeSelector } from '@cistec/common_web/utils/store';
import { useScrollToAddedElement } from '@cistec/common_web/hooks/useScrollToAddedElement';

type Props = {
    children: ReactElement<any> | ReactElement<any>[];
    cis4mData: CIS4mData;
};

export const getECheckinSectionsCSS = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    height: 100%;

    .eCheckinSectionsHeader {
        display: flex;
        justify-content: right;
    }

    .eCheckinSectionWrapper {
        flex: auto;
        padding: 16px;
        overflow-y: auto;

        h1 {
            font-size: 1.5em;
            font-weight: 400;
            margin-bottom: 24px;
        }

        ul {
            padding-inline-start: 16px;
            padding: 0 0 0 16px;
        }
    }

    .eCheckinSectionsFooter {
        flex: none;
        margin: 0;
        padding: 16px;
        box-shadow: 0 -8px 8px rgb(50 50 50 / 10%);

        .progressStepper {
            margin-bottom: 16px;
        }

        .eCheckinSectionsButtonsWrapper {
            display: flex;
            justify-content: right;

            &.multiple {
                justify-content: space-between;
            }
        }
    }
`;

const FIELD_IS_CONFIRMED = 'isComplete';

const isSectionConfirmed = (eCheckinItem: ECheckinWorkItem, sectionPath: string) => {
    return _.get(eCheckinItem, ['CONTENT', 'echeckin', sectionPath, FIELD_IS_CONFIRMED]) === '1';
};

export const ECheckinSections = forwardRef((props: Props, ref: any) => {
    const { children, cis4mData } = props;
    const formId = cis4mData.id;

    const dispatch = useDispatch<any>();

    const theme = useTheme();
    const { formatMessage } = useIntl();

    const { fallNr, tKey } = useSelector(selectSelectedECheckin);
    const { data: eCheckinItem } = useECheckinItem(fallNr, tKey);

    const isFormChanged = !!cis4mData.lastChanged;
    const saveStatus = cis4mData.requestStatus;
    const isSaving = saveStatus === RequestStatus.LOADING;

    const finalizeStatus = useMadeSelector(() => makeSelectFinalizeECheckinRequestStatus(fallNr, tKey), [fallNr, tKey]);
    const isFinalizing = finalizeStatus === RequestStatus.LOADING;

    const formSections: ReactElement<any>[] = Array.isArray(children) ? children : [children];

    const sectionCount = formSections.length;
    const sectionPaths = formSections.map((child: ReactElement<any>) => child.props.path as string);

    const confirmedSections = eCheckinItem
        ? sectionPaths.filter(sectionPath => isSectionConfirmed(eCheckinItem, sectionPath))
        : [];

    const firstUnconfirmedSectionIndex = sectionPaths.findIndex(
        sectionPath => !confirmedSections.includes(sectionPath)
    );

    const lastConfirmedSectionIndex = sectionPaths.reduce(
        (lastIndex, sectionPath, index) => (confirmedSections.includes(sectionPath) ? index : lastIndex),
        -1
    );

    const [selectedSectionIndex, setSelectedSectionIndex] = useState(
        firstUnconfirmedSectionIndex === -1 ? sectionCount - 1 : firstUnconfirmedSectionIndex
    );

    const selectedSectionPath = sectionPaths[selectedSectionIndex];

    // have to get whether data has been confirmed from the work item!!
    const isSelectedSectionConfirmed = eCheckinItem ? isSectionConfirmed(eCheckinItem, selectedSectionPath) : false;

    const sectionsCompletionData = sectionPaths.map(
        sectionPath =>
            confirmedSections.includes(sectionPath) && !(sectionPath === selectedSectionPath && isFormChanged)
    );

    const [selectedIndexWhenSavedSuccessfully, setSelectedIndexWhenSavedSuccessfully] = useState(-1);
    const [closeWhenSavedSuccessfully, setCloseWhenSavedSuccessfully] = useState(false);
    const [closeWhenFinalisedSuccessfully, setCloseWhenFinalisedSuccessfully] = useState(false);
    const [finaliseWhenSavedSuccessfully, setFinaliseWhenSavedSuccessfully] = useState(false);

    const formSectionToRender = formSections[selectedSectionIndex];

    const sectionsArray = formSectionToRender ? [formSectionToRender] : [];
    const connectedECheckinSections = useConnectToChildCIS4mComponents(ref, sectionsArray);

    const close = () => {
        dispatch(actionRemoveCIS4m(formId));
    };

    const save = () => {
        if (isSelectedSectionConfirmed && isFormChanged) {
            dispatch(actionRemoveFromCIS4mDataObject(formId, createFieldPath(selectedSectionPath, FIELD_IS_CONFIRMED)));
        }
        dispatch(actionSaveCIS4m(formId, ref.current));
    };

    const saveAndClose = () => {
        setCloseWhenSavedSuccessfully(true);
        save();
    };

    const confirmAndSave = (onValidate?: (success: boolean) => void) => {
        const handleValidate = (success: boolean) => {
            onValidate?.(success);
            if (success) {
                dispatch(
                    actionSetInCIS4mDataObject(formId, createFieldPath(selectedSectionPath, FIELD_IS_CONFIRMED), '1')
                );
            } else {
                scrollToError();
            }
        };

        // set confirmation in store
        dispatch(actionValidateAndSaveCIS4m(formId, ref.current, handleValidate));
    };

    useEffect(() => {
        if (selectedIndexWhenSavedSuccessfully === -1 || isSaving) return;
        if (saveStatus === RequestStatus.SUCCESS) {
            setSelectedSectionIndex(selectedIndexWhenSavedSuccessfully);
        }
        setSelectedIndexWhenSavedSuccessfully(-1);
    }, [selectedIndexWhenSavedSuccessfully, saveStatus]);

    useEffect(() => {
        if (!closeWhenSavedSuccessfully || isSaving) return;
        if (saveStatus === RequestStatus.SUCCESS) {
            close();
        }
        setCloseWhenSavedSuccessfully(false);
    }, [closeWhenSavedSuccessfully, saveStatus]);

    useEffect(() => {
        if (!closeWhenFinalisedSuccessfully || isFinalizing) return;
        if (finalizeStatus === RequestStatus.SUCCESS) {
            close();
            setCloseWhenFinalisedSuccessfully(false);
        }
    }, [closeWhenFinalisedSuccessfully, finalizeStatus]);

    useEffect(() => {
        if (!finaliseWhenSavedSuccessfully || isSaving) return;
        if (saveStatus === RequestStatus.SUCCESS) {
            setCloseWhenFinalisedSuccessfully(true);
            dispatch(actionFinalizeECheckin(eCheckinItem!));
        }
        setFinaliseWhenSavedSuccessfully(false);
    }, [finaliseWhenSavedSuccessfully, saveStatus]);

    const [scrollToError] = useScrollToAddedElement({ element: '.Mui-error', root: '.eCheckinSectionWrapper' });

    const renderCancelButton = () => {
        const isVisible = selectedSectionIndex === 0;
        if (!isVisible) return null;

        const handleCancelClick = () => {
            close();
        };

        return (
            <Button onClick={handleCancelClick} key="cancel">
                {formatMessage(textMap.cancel)}
            </Button>
        );
    };

    const renderPreviousButton = () => {
        const isVisible = selectedSectionIndex > 0;
        if (!isVisible) return null;

        const isDisabled =
            isSaving ||
            isFormChanged ||
            (!isSelectedSectionConfirmed && selectedSectionIndex < lastConfirmedSectionIndex);

        const handlePreviousClick = () => {
            setSelectedSectionIndex(selectedSectionIndex - 1);
        };

        return (
            <Button onClick={handlePreviousClick} disabled={isDisabled} key="previous">
                {formatMessage(textMap.back)}
            </Button>
        );
    };


    const renderNextButton = () => {
        const isVisible = selectedSectionIndex < sectionCount - 1;
        if (!isVisible) return null;

        const isDisabled = isSaving;

        const handleNextClick = () => {
            const newSectionIndex = selectedSectionIndex + 1;

            if (isFormChanged || !isSelectedSectionConfirmed) {
                confirmAndSave(success => {
                    if (success) {
                        setSelectedIndexWhenSavedSuccessfully(newSectionIndex);
                    }
                });
            } else {
                setSelectedSectionIndex(newSectionIndex);
            }
        };

        return (
            <Button onClick={handleNextClick} disabled={isDisabled} variant="contained" color="primary" key="next">
                {isFormChanged || !isSelectedSectionConfirmed
                    ? formatMessage(textMap.confirm)
                    : formatMessage(textMap.continue)}
            </Button>
        );
    };

    const renderFinaliseButton = () => {
        const isVisible = selectedSectionIndex === sectionCount - 1;
        if (!isVisible) return null;

        const isDisabled = isSaving || isFinalizing;

        const handleFinaliseClick = () => {
            confirmAndSave(success => {
                if (success) {
                    setFinaliseWhenSavedSuccessfully(true);
                }
            });
        };

        return (
            <Button
                onClick={handleFinaliseClick}
                disabled={isDisabled}
                variant="contained"
                color="primary"
                key="finalise"
            >
                {formatMessage(textMap.checkinAbschliessen)}
            </Button>
        );
    };

    const renderClearButton = () => {
        if (!['development', 'test'].includes(process.env.NODE_ENV)) return null;
        const handleClearClick = () => {
            if (eCheckinItem) {
                dispatch(actionUpdateECheckin(eCheckinItem, {}));
                close();
            }
        };
        return (
            <Button onClick={handleClearClick} key="clear">
                CLEAR ECHECKIN
            </Button>
        );
    };

    const renderCloseButton = () => {
        if (isFormChanged) return null;

        const handleClick = () => {
            close();
        };

        return (
            <Button onClick={handleClick} key="close">
                {formatMessage(textMap.close)}
                <CloseIcon />
            </Button>
        );
    };

    const renderSaveAndCloseButton = () => {
        if (!isFormChanged) return null;

        const handleClick = () => {
            if (isFormChanged) {
                saveAndClose();
            } else {
                close();
            }
        };

        return (
            <Button onClick={handleClick} disabled={isSaving} key="saveAndClose">
                {formatMessage(textMap.saveAndClose)}
                <CloseIcon />
            </Button>
        );
    };

    const renderProgressStepper = () => {
        const handleStepClick = (stepNumber: number) => {
            if (stepNumber === selectedSectionIndex) return;
            // cannot use stepper for steps further
            if (stepNumber > firstUnconfirmedSectionIndex && !isSelectedSectionConfirmed) return;
            // cannot use stepper if form has changed
            if (
                isFormChanged ||
                (!isSelectedSectionConfirmed &&
                    !(
                        selectedSectionIndex === firstUnconfirmedSectionIndex &&
                        firstUnconfirmedSectionIndex > lastConfirmedSectionIndex
                    ))
            ) {
                const isValid = validateForm(ref.current);
                if (!isValid) {
                    scrollToError();
                }
                return;
            }

            setSelectedSectionIndex(stepNumber);
        };

        return (
            <ProgressStepper
                className="progressStepper"
                currentStep={selectedSectionIndex}
                stepData={sectionsCompletionData}
                onStepClick={handleStepClick}
            />
        );
    };

    const headerButtons = _.compact([renderClearButton(), renderCloseButton(), renderSaveAndCloseButton()]);

    const footerButtons = _.compact([
        renderCancelButton(),
        renderPreviousButton(),
        renderNextButton(),
        renderFinaliseButton(),
    ]);

    const renderSection = () => {
        return connectedECheckinSections;
    };

    return (
        <div className={getECheckinSectionsCSS(theme)}>
            <div className="eCheckinSectionsHeader">{headerButtons}</div>
            <div className="eCheckinSectionWrapper">{renderSection()}</div>
            <div className="eCheckinSectionsFooter">
                {renderProgressStepper()}
                <div className={cx('eCheckinSectionsButtonsWrapper', { multiple: footerButtons.length >= 2 })}>
                    {footerButtons}
                </div>
            </div>
        </div>
    );
});

ECheckinSections.displayName = 'ECheckinSections';

export default ECheckinSections;
